import { Check } from 'lucide-react';

import { Button } from '@/shared/ui/button';
import { Pill } from '@/shared/ui/pill';

import { formatAmount, formatDate } from '../billing-format';

import type { BillingPlan, BillingPlanId, BillingStatus } from '@mio/shared';

const FEATURES = [
  'Every Studio lesson, worked trace and example',
  'Workshop IDE labs with Mio beside you',
  'Workbench: the full client engagement simulation',
  'Mio mentor in chat and voice',
  '$50 in shared AI credits every month',
  '4 hours of ElevenLabs voice every month',
];
const LIVE = new Set(['active', 'trialing', 'past_due']);

export function PlanCards({
  plans,
  status,
  busyPlan,
  disabled = false,
  onSelect,
}: {
  plans: readonly BillingPlan[];
  status: BillingStatus | undefined;
  busyPlan: BillingPlanId | null;
  disabled?: boolean;
  onSelect: (plan: BillingPlanId) => void;
}) {
  const subscriptionLive = Boolean(status?.subscription && LIVE.has(status.subscription.status));
  return (
    <div className="plan-cards">
      {plans.map((plan) => {
        const current =
          plan.id === 'monthly' ? subscriptionLive : Boolean(status?.pass?.plan === plan.id);
        const label =
          plan.id === 'monthly'
            ? current
              ? 'Current plan'
              : 'Choose monthly'
            : current
              ? 'Buy another pass'
              : 'Buy 6-month pass';
        return (
          <section
            key={plan.id}
            className="plan-card"
            data-current={current}
            aria-labelledby={`plan-${plan.id}`}
          >
            <div className="flex items-center justify-between gap-3">
              <h3 id={`plan-${plan.id}`} className="plan-card-name">
                {plan.name}
              </h3>
              {current ? (
                <Pill tone="primary">
                  {plan.id === 'six_month' && status?.pass
                    ? `Until ${formatDate(status.pass.accessUntil)}`
                    : 'Current'}
                </Pill>
              ) : null}
            </div>
            <p className="plan-card-price">
              <strong>{formatAmount(plan)}</strong>
              <span>{plan.interval ? 'per month' : 'one-time · 6 months'}</span>
            </p>
            <p className="plan-card-summary">{plan.summary}</p>
            <ul className="plan-card-features">
              {FEATURES.map((feature) => (
                <li key={feature}>
                  <Check className="size-3.5" aria-hidden="true" />
                  {feature}
                </li>
              ))}
            </ul>
            <div className="plan-card-action">
              <Button
                type="button"
                variant={plan.id === 'monthly' ? 'primary' : 'secondary'}
                className="w-full"
                disabled={disabled || (plan.id === 'monthly' && current)}
                loading={busyPlan === plan.id}
                loadingLabel="Opening checkout…"
                onClick={() => onSelect(plan.id)}
              >
                {label}
              </Button>
            </div>
          </section>
        );
      })}
      <p className="plan-tax-note sm:col-span-2">
        AI credits and voice time renew monthly on both plans, including the 6-month pass. Prices in
        USD before tax. Sales tax, VAT or GST is added at checkout where it applies. Payments are
        handled by Stripe.
      </p>
    </div>
  );
}
